import { useGetEnrollmentWelcomeIraData, useSubmitEnrollmentWelcomeIraData } from '@egw/api'
import { Alert, AlertType, GenericError, Button } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { EnrollmentV2Layout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { CheckCircleIcon, CurrencyDollarIcon, ChartPieIcon } from '@heroicons/react/24/outline'
import { useEffect, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'
import { PageHeader, WelcomeStepV2 } from '../../../../components'
import { AccountTypeEnum } from '../../../../constants/api/account-type-enum'
import { useValidateRoute } from '../../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Welcome Ira')

export const WelcomeIraPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const { accountType } = useParams<{ accountType: string }>()

  const { data, isLoading, error } = useGetEnrollmentWelcomeIraData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentWelcomeIraData()

  // Protect route
  useEffect(() => {
    if (
      accountType !== AccountTypeEnum.TRADITIONAL &&
      accountType !== AccountTypeEnum.ROTH &&
      accountType !== AccountTypeEnum.BROKERAGE &&
      accountType !== AccountTypeEnum.TRADITIONAL_INHERITED &&
      accountType !== AccountTypeEnum.ROTH_INHERITED
    ) {
      log.error('Invalid account type for Welcome IRA page:', accountType)
      navigate(WEALTH_ROUTES.INVESTMENTS.path)
    }
  }, [accountType, navigate])

  useValidateRoute(data?.path)

  const closeHandler = async (): Promise<void> => {
    try {
      await mutate({
        abandonApplication: true,
      })
      navigate(WEALTH_ROUTES.INVESTMENTS.path)
    } catch (e) {
      log.error('Error abandoning application', e)
    }
  }

  // Determine which title key to use based on accountType
  const getTitleKey = (): string => {
    if (accountType === AccountTypeEnum.BROKERAGE) {
      return 'investments_v2.welcome.title.brokerage_yeet_coastal'
    }
    return `investments_v2.welcome.title.${accountType ?? ''}`
  }

  const pageTitle = t(getTitleKey())
  const congratsMessage = t(`investments_v2.welcome.p1.${accountType ?? ''}`)
  const description = t(`investments_v2.welcome.p2.${accountType ?? ''}`)

  return (
    <EnrollmentV2Layout closeHandler={closeHandler}>
      <div className="h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <>
            <CheckCircleIcon className="h-[4rem] w-[4rem] stroke-textSuccess stroke-[.5px] mb-sm" />
            <PageHeader title={pageTitle} textClassName="text-textSuccess" />

            {accountType === AccountTypeEnum.BROKERAGE ? (
              <>
                <WelcomeStepV2
                  icon={<CurrencyDollarIcon className="size-6" />}
                  testId="fund-checking"
                  title={t('investments_v2.welcome.brokerage_steps.step_1.title_yeet_coastal')}
                  description={t('investments_v2.welcome.brokerage_steps.step_1.description')}
                />
                <WelcomeStepV2
                  icon={<ChartPieIcon className="size-6" />}
                  testId="earn-with-savings"
                  title={t('investments_v2.welcome.brokerage_steps.step_2.title')}
                  description={t('investments_v2.welcome.brokerage_steps.step_2.description')}
                />
              </>
            ) : (
              <>
                <p className="typo-primary-base-regular mb-md">{congratsMessage}</p>
                <p className="typo-primary-base-regular mb-md">{description}</p>
              </>
            )}

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            <Button
              type="button"
              className="w-full md:w-min mt-lg"
              isLoading={isSubmitting}
              testId="enrollment-investments-v2-continue-button"
              onClick={() => {
                navigate(WEALTH_ROUTES.OVERVIEW.path)
              }}
            >
              {t('investments_v2.welcome.go_to_accounts')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentV2Layout>
  )
}
